import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { AssigneeOptions } from '@/components/AssigneeOptions.js';
import { LinkIndicator } from '@/components/LinkIndicator.js';
import { EditableStatusCell, EditableAssigneeCell } from '@/components/InlineEditCells.js';
import { useCreateChildIssue } from '@/hooks/useIssues.js';
import { useAssignableUsers, useProjectIssueTypes } from '@/hooks/useMeta.js';
import { childProgress } from '@/lib/child-issues.js';
import type { JiraChildIssue } from '@shared/types.js';

interface ChildIssuesProps {
  issueKey: string;
  projectKey: string;
  /** subtask: under a task. child: work items under an epic. */
  kind: 'subtask' | 'child';
  items: JiraChildIssue[];
  /** side: a card in the narrow right column. main: a full-width list under the description. */
  placement: 'side' | 'main';
}

/** Jira's "Subtasks" / "Child work items" panel: progress, status and assignee in place, inline add. */
export function ChildIssues({ issueKey, projectKey, kind, items, placement }: ChildIssuesProps) {
  const [adding, setAdding] = useState(false);
  const { data: users } = useAssignableUsers(projectKey);
  const title = kind === 'subtask' ? 'Subtasks' : 'Child work items';
  const addLabel = kind === 'subtask' ? 'Add subtask' : 'Add child work item';
  const progress = childProgress(items);

  if (placement === 'side') {
    return (
      <section className="rounded-lg border bg-white" aria-label={title}>
        <div className="flex items-center justify-between border-b px-4 py-2">
          <h3 className="text-sm font-semibold text-gray-800">
            {title}
            {items.length > 0 && (
              <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{items.length}</span>
            )}
          </h3>
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="-mr-1 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title={addLabel}
              aria-label={addLabel}
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                <path d="M8 3v10M3 8h10" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {items.length > 0 && (
          <div className="flex items-center gap-2 px-4 pt-3 pb-1" title={`${progress.done} of ${progress.total} done`}>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done} aria-label={`${title} done`}>
              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${progress.percent}%` }} />
            </div>
            <span className="whitespace-nowrap text-xs text-gray-500">{progress.done} of {progress.total} done</span>
          </div>
        )}

        {items.length > 0 && (
          <ul className="py-1.5">
            {items.map((item) => (
              <li key={item.key} className="px-4 py-1.5 hover:bg-gray-50">
                <div className="flex items-center gap-1.5">
                  <IssueTypeIcon name={item.issuetype.name} />
                  <Link to={`/issues/${item.key}`} className={`shrink-0 text-xs font-semibold text-blue-600 hover:underline ${item.status.category === 'done' ? 'text-gray-500 line-through' : ''}`}>
                    {item.key}
                  </Link>
                  <LinkIndicator links={item.links} />
                  <div className="min-w-0 flex-1 text-xs">
                    <EditableAssigneeCell issue={item} users={users ?? []} />
                  </div>
                  <EditableStatusCell issue={item} menuAlign="right" />
                </div>
                <Link
                  to={`/issues/${item.key}`}
                  className={`mt-0.5 block text-sm leading-snug line-clamp-2 hover:text-blue-700 ${item.status.category === 'done' ? 'text-gray-500' : 'text-gray-800'}`}
                  title={item.summary}
                >
                  {item.summary}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {adding ? (
          <AddChildForm issueKey={issueKey} projectKey={projectKey} kind={kind} placement="side" onDone={() => setAdding(false)} />
        ) : items.length === 0 && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="w-full rounded-b-lg px-4 py-3 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          >
            {addLabel}
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="mt-6" aria-label={title}>
      <div className="mb-2 flex items-center gap-3">
        <h3 className="text-sm font-semibold text-gray-800">
          {title}
          {items.length > 0 && (
            <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{items.length}</span>
          )}
        </h3>
        {items.length > 0 && (
          <div className="flex min-w-0 flex-1 items-center gap-2" title={`${progress.done} of ${progress.total} done`}>
            <div className="h-1.5 max-w-48 flex-1 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done} aria-label={`${title} done`}>
              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${progress.percent}%` }} />
            </div>
            <span className="whitespace-nowrap text-xs text-gray-500">{progress.done} of {progress.total} done</span>
          </div>
        )}
        {items.length === 0 && <span className="flex-1" />}
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="-mr-1 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            title={addLabel}
            aria-label={addLabel}
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path d="M8 3v10M3 8h10" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {items.length > 0 && (
        <ul className="divide-y rounded-lg border bg-white">
          {items.map((item) => (
            <li key={item.key} className="flex items-center gap-2 px-3 py-1.5 text-sm first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50">
              <IssueTypeIcon name={item.issuetype.name} />
              <Link to={`/issues/${item.key}`} className={`shrink-0 font-medium text-blue-600 hover:underline ${item.status.category === 'done' ? 'text-gray-500 line-through' : ''}`}>
                {item.key}
              </Link>
              <Link to={`/issues/${item.key}`} className="min-w-0 flex-1 truncate text-gray-800 hover:text-blue-700" title={item.summary}>{item.summary}</Link>
              <LinkIndicator links={item.links} />
              <div className="hidden w-40 shrink-0 sm:block">
                <EditableAssigneeCell issue={item} users={users ?? []} />
              </div>
              <div className="flex shrink-0 justify-end sm:w-28">
                <EditableStatusCell issue={item} menuAlign="right" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <AddChildForm issueKey={issueKey} projectKey={projectKey} kind={kind} placement={items.length > 0 ? 'main' : 'main-empty'} onDone={() => setAdding(false)} />
      ) : items.length === 0 && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
        >
          {addLabel}
        </button>
      )}
    </section>
  );
}

interface AddChildFormProps {
  issueKey: string;
  projectKey: string;
  kind: 'subtask' | 'child';
  placement: 'side' | 'main' | 'main-empty';
  onDone: () => void;
}

/** Stays open after each add so several can be entered in a row. */
function AddChildForm({ issueKey, projectKey, kind, placement, onDone }: AddChildFormProps) {
  const [summary, setSummary] = useState('');
  const [assignee, setAssignee] = useState('');
  const [issueType, setIssueType] = useState('Task');
  const { data: users } = useAssignableUsers(projectKey);
  const { data: types } = useProjectIssueTypes(kind === 'child' ? projectKey : '');
  const create = useCreateChildIssue(issueKey, projectKey);
  // An epic can't sit under an epic
  const typeOptions = (types ?? []).filter((t) => t.name.toLowerCase() !== 'epic');

  const side = placement === 'side';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = summary.trim();
    if (!text || create.isPending) return;
    create.mutate(
      { summary: text, assignee, issuetype: kind === 'child' ? issueType : undefined },
      { onSuccess: () => setSummary('') },
    );
  }

  return (
    <form onSubmit={handleSubmit} className={side ? 'rounded-b-lg border-t bg-gray-50 p-3' : `rounded-lg border bg-gray-50 p-3 ${placement === 'main' ? 'mt-2' : ''}`}>
      <div className={`flex flex-col gap-2 ${side ? '' : 'sm:flex-row'}`}>
        {kind === 'child' && typeOptions.length > 1 && (
          <select
            value={issueType}
            onChange={(e) => setIssueType(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
            aria-label="Type"
          >
            {typeOptions.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
          </select>
        )}
        <input
          type="text"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onDone(); }}
          placeholder="What needs to be done?"
          maxLength={255}
          className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          aria-label={kind === 'subtask' ? 'Subtask summary' : 'Child work item summary'}
          autoFocus
        />
        <select
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className={`rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm ${side ? '' : 'sm:w-44'}`}
          aria-label="Assignee"
        >
          <AssigneeOptions users={users} />
        </select>
      </div>
      {create.data?.reporterWarning && <p role="status" className="mt-2 text-xs text-amber-700">{create.data.reporterWarning}</p>}
      {create.isError && (
        <p className="mt-2 text-xs text-red-600">Couldn&apos;t add it: {create.error.message}</p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">
          Close
        </button>
        <button
          type="submit"
          disabled={!summary.trim() || create.isPending}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {create.isPending ? 'Adding...' : 'Add'}
        </button>
      </div>
    </form>
  );
}
